import React, { useState } from 'react';
import Dialog from '@mui/material/Dialog';
import { 
  Activity, 
  BarChart3, 
  FileText, 
  Info,
  Layout,
  TrendingDown,
  TrendingUp,
  AlertCircle,
  BarChart,
  Timer
} from 'lucide-react';
import styles from '../PortfolioScore/PortfolioScore.module.sass'

interface NavButtonProps {
  icon: React.ReactNode;
  label: string;
  active: boolean;
  onClick: () => void;
}

const NavButton: React.FC<NavButtonProps> = ({ icon, label, active, onClick }) => (
  <button
    className={`${styles.navButton} ${active ? styles.active : ''}`}
    onClick={onClick}
  >
    {icon}
    {label}
  </button>
);

interface PortfolioScoreModalProps {
  open: boolean;
  onClose: () => void;
  score: number;
  marketScore?: number;
  drawdownScore?: number;
}

const PortfolioScoreModal: React.FC<PortfolioScoreModalProps> = ({ 
  open, 
  onClose, 
  score, 
  marketScore = 3, 
  drawdownScore = 3 
}) => {
  const [activeTab, setActiveTab] = useState('overview');

  const getScoreColor = (score: number) => {
    if (score < 2) return '#f44336';  // Red
    if (score < 3.5) return '#9c27b0'; // Purple
    return '#73F6FF';  // Cyan
  };

  const getScoreCategory = (score: number) => {
    if (score >= 4) return 'STRONG PERFORMANCE';
    if (score >= 3) return 'FAVORABLE';
    if (score >= 2) return 'STANDARD';
    return 'REVIEW SUGGESTED';
  };

  const getScoreDescription = (score: number) => {
    if (score >= 4) return 'Your portfolio is performing exceptionally well with optimal risk management.';
    if (score >= 3) return 'Your portfolio is performing well with good risk/return characteristics.';
    if (score >= 2) return 'Your portfolio has moderate performance with some risk concerns.';
    return 'Your portfolio would benefit from a review to address performance and risk issues.';
  };

  const renderContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <>
            <div className={styles.infoSection}>
              <h3>
                About Portfolio Score
              </h3>
              <p>
                The Portfolio Score is a comprehensive metric that evaluates your portfolio's health by analyzing 
                market performance and drawdown characteristics. This score ranges from 1 to 5, combining equal 
                weightings of market comparison and risk metrics.
              </p>
              <p>
                Higher scores indicate stronger performance and better risk management, while lower scores suggest areas 
                that may need attention for portfolio optimization.
              </p>
            </div>
            <div className={styles.infoSection}>
              <h3>
                Score Components
              </h3>
              <p>
                Your current score of <strong>{score.toFixed(2)}</strong> is calculated by averaging:
              </p>
              <div className={styles.factorsList}>
                <div className={styles.factorItem}>
                  Market Score: <strong>{marketScore.toFixed(2)}</strong>
                </div>
                <div className={styles.factorItem}>
                  Drawdown Score: <strong>{drawdownScore.toFixed(2)}</strong>
                </div>
              </div>
            </div>
            <div className={styles.infoSection}>
              <h3>
                Performance Summary
              </h3>
              <p>
                Your portfolio is currently rated as <strong style={{ color }}>{category}</strong>.
              </p>
              <p>{description}</p>
              <p>
                Regular monitoring and periodic rebalancing can help maintain or improve your score over time.
              </p>
            </div>
          </>
        );
      case 'market':
        return (
          <>
            <div className={styles.infoSection}>
              <h3>
                Market Performance Score
              </h3>
              <p>
                The Market Score evaluates how your portfolio performs compared to a selected benchmark. 
                It analyzes outperformance, volatility, and return consistency.
              </p>
            </div>
            <div className={styles.infoSection}>
              <h3>
                Key Factors
              </h3>
              <div className={styles.factorsList}>
                <div className={styles.factorItem}>
                  Relative Return
                </div>
                <div className={styles.factorItem}>
                  Volatility Ratio
                </div>
                <div className={styles.factorItem}>
                  Alpha Generation
                </div>
                <div className={styles.factorItem}>
                  Consistency Index
                </div>
              </div>
            </div>
            <div className={styles.infoSection}>
              <h3>
                Interpreting Your Score
              </h3>
              <p>
                A high market score (4-5) indicates consistent outperformance of your benchmark with controlled 
                volatility. A score of 3 suggests performance in line with the benchmark. Lower scores indicate 
                underperformance or excessive volatility relative to returns.
              </p>
            </div>
          </>
        );
      case 'drawdown':
        return (
          <>
            <div className={styles.infoSection}>
              <h3>
                Drawdown Risk Score
              </h3>
              <p>
                The Drawdown Score assesses your portfolio's resilience during market downturns. It evaluates maximum 
                drawdowns, recovery periods, and drawdown frequency compared to industry benchmarks.
              </p>
            </div>
            <div className={styles.infoSection}>
              <h3>
                Key Metrics
              </h3>
              <div className={styles.factorsList}>
                <div className={styles.factorItem}>
                  Max Drawdown
                </div>
                <div className={styles.factorItem}>
                  Recovery Time
                </div>
                <div className={styles.factorItem}>
                  Drawdown Frequency
                </div>
                <div className={styles.factorItem}>
                  Benchmark Correlation
                </div>
              </div>
            </div>
            <div className={styles.infoSection}>
              <h3>
                Interpreting Your Score
              </h3>
              <p>
                A high drawdown score (4-5) indicates your portfolio maintains value well during market corrections 
                with quick recovery times. A score of 3 represents average downside protection. Lower scores suggest 
                deeper drawdowns or longer recovery periods than comparable portfolios.
              </p>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const color = getScoreColor(score);
  const category = getScoreCategory(score);
  const description = getScoreDescription(score);

  // Apply MUI overrides via inline styles to fix scrollbars
  const muiOverrideStyles = {
    paper: { overflow: 'hidden !important' },
    scrollPaper: { overflow: 'hidden !important' },
    container: { overflow: 'hidden !important' },
    root: { overflow: 'hidden !important' }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      PaperProps={{
        className: styles.modalPaper,
        style: { overflow: 'hidden !important' }
      }}
      maxWidth={false}
      scroll="paper"
      style={{ overflow: 'hidden !important' }}
      classes={{
        paper: muiOverrideStyles.paper,
        scrollPaper: muiOverrideStyles.scrollPaper,
        container: muiOverrideStyles.container,
        root: muiOverrideStyles.root
      }}
    >
      <div className={styles.container}>
        <div className={styles.sidebar}>
          <div className={styles.header}>
            <div className={styles.title}>Portfolio Score</div>
            <div className={styles.subtitle}>Performance Analysis</div>
          </div>
          
          <div className={styles.nav}>
            <NavButton
              icon={<Layout size={18} />}
              label="Overview"
              active={activeTab === 'overview'}
              onClick={() => setActiveTab('overview')}
            />
            <NavButton
              icon={<BarChart3 size={18} />}
              label="Market Performance"
              active={activeTab === 'market'}
              onClick={() => setActiveTab('market')}
            />
            <NavButton
              icon={<TrendingDown size={18} />}
              label="Drawdown Analysis"
              active={activeTab === 'drawdown'}
              onClick={() => setActiveTab('drawdown')}
            />
          </div>

          <div className={styles.scoreSection}>
            <div className={styles.scoreCircle}>
              <div className={styles.scoreNumber}>{score.toFixed(2)}</div>
              <div style={{ fontSize: '0.75rem', color: 'rgba(115, 246, 255, 0.8)' }}>/5.00</div>
            </div>
            
            <div className={styles.scoreInfo}>
              <div className={styles.description}>
                {description}
              </div>
            </div>
          </div>
        </div>

        <div className={styles.main}>
          <div className={styles.content}>
            {renderContent()}
          </div>
        </div>
      </div>
    </Dialog>
  );
};

export default PortfolioScoreModal;